import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import './AiResultModal.css';

/**
 * AI生成結果表示モーダル（汎用コンポーネント）
 *
 * Markdown形式のAI生成結果を表示する再利用可能なモーダル。
 * ローディング・結果表示・エラーの3つの状態を管理。
 *
 * @param {Object} props
 * @param {boolean} props.loading - ローディング中かどうか
 * @param {string} props.loadingMessage - ローディング中に表示するメッセージ（デフォルト: "AIが生成中..."）
 * @param {string|null} props.result - AI生成結果（Markdown文字列）
 * @param {string} props.title - 結果モーダルのタイトル（デフォルト: "✨ AI生成結果"）
 * @param {string|null} props.error - エラーメッセージ
 * @param {Function} props.onClose - 結果モーダルを閉じるコールバック
 * @param {Function} props.onErrorClose - エラーモーダルを閉じるコールバック
 */
function AiResultModal({
    loading,
    loadingMessage = 'AIが生成中...',
    result,
    title = '✨ AI生成結果',
    error,
    onClose,
    onErrorClose,
}) {
    const [copied, setCopied] = useState(false);
    // ローディングオーバーレイ
    if (loading) {
        return (
            <div className="loading-popup-overlay">
                <div className="loading-popup-content">
                    <div className="spinner"></div>
                    <span>{loadingMessage}</span>
                </div>
            </div>
        );
    }

    // エラーモーダル
    if (error) {
        return (
            <div className="ai-modal-overlay" onClick={onErrorClose}>
                <div className="ai-modal-container" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
                    <div className="ai-modal-header">
                        <h3>エラー</h3>
                        <button className="ai-modal-close" onClick={onErrorClose} aria-label="閉じる">×</button>
                    </div>
                    <div className="ai-modal-body">
                        <p className="ai-error-text">{error}</p>
                    </div>
                    <div className="ai-modal-footer">
                        <button className="modal-button-confirm" onClick={onErrorClose}>
                            閉じる
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 結果モーダル
    if (result) {
        const handleCopy = async () => {
            try {
                await navigator.clipboard.writeText(result);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch {
                // fallback
                const textarea = document.createElement('textarea');
                textarea.value = result;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        };

        return (
            <div className="ai-modal-overlay">
                <div className="ai-modal-container" onClick={e => e.stopPropagation()}>
                    <div className="ai-modal-header">
                        <h3>{title}</h3>
                        <button className="ai-modal-close" onClick={onClose} aria-label="閉じる">×</button>
                    </div>
                    <div className="ai-modal-body">
                        <div className="ai-markdown">
                            <ReactMarkdown remarkPlugins={[remarkBreaks]}>{result}</ReactMarkdown>
                        </div>
                    </div>
                    <div className="ai-modal-footer">
                        <button className="ai-copy-button" onClick={handleCopy}>
                            {copied ? '✓ コピーしました' : '📋 コピー'}
                        </button>
                        <button className="modal-button-confirm" onClick={onClose}>
                            閉じる
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}

export default AiResultModal;
